// ============ Global CRM Constants ============
// Centralized constants for consistent use across the app

// Area options (global enum)
export const AREAS = ["PTA", "JHB", "PE", "WTB", "CPT"];

// ============ MEDICAL AID OPTIONS ============
// Fixed list of South African medical aid schemes for referral forms
export const MEDICAL_AID_OPTIONS = [
  "AECI",
  "ALLIANCE MIDMED",
  "Anglo",
  "Banked",
  "Bestmed",
  "BMW EMPLOYEES MEDICAL",
  "Bonitas",
  "CAMAF",
  "Compcare",
  "Discovery",
  "Fedhealth",
  "GEMS",
  "Glencore",
  "Imperial",
  "LA Health",
  "LIBCARE MEDICAL SCHEME",
  "Malcor",
  "Massmart",
  "Medihelp",
  "MEDIMED",
  "Medipos",
  "Medshield",
  "Momentum",
  "MotoHealth",
  "NETCARE",
  "Old Mutal",
  "Opmed",
  "Parmed",
  "Pick 'n pay medscheme",
  "Platinum",
  "Polmed",
  "PRIVATE",
  "PROFMED",
  "Rand water",
  "Remedi",
  "SAB",
  "SABC",
  "SAND",
  "Sasolmed",
  "SISONKE HEALTH",
  "Sizwe Hosmed",
  "TFG MEDICAL SCHEME",
  "Thebemed",
  "TIGER BRANDS",
  "UMVUZO",
  "Witbank Coalfields",
  "Wooltru"
];

// ============ APPROVED REP DIRECTORY ============
// This is the ONLY source of truth for valid rep names
// All rep dropdowns and backend validation use this list
// NOTE: Lian Botha is management, NOT a rep - excluded from this list
export const REP_DIRECTORY = [
  "Anel Swart",
  "Garth Bolton",
  "Frans Schutte",
  "Corine Wheeler"
];

// Legacy rep IDs (for backward compatibility - DO NOT USE for new records)
export const LEGACY_REP_IDS = ["A", "B", "C", "D"];

// Legacy mapping (internal ID -> display name) - for display purposes only
export const REP_DISPLAY_NAMES = {
  "A": "Anel Swart",
  "B": "Garth Bolton",
  "C": "Frans Schutte",
  "D": "Corine Wheeler"
};

// Junk/test rep names that should be cleaned up
export const INVALID_REP_NAMES = [
  "A", "B", "C", "D",
  "Test Rep",
  "Test User",
  "Calendar Test User",
  "Test User Visit Calendar",
  "test",
  "Lian Botha"  // Management, not a rep
];

// Helper: Check if a rep name is valid (in the approved directory)
export const isValidRepName = (repName) => {
  if (!repName) return false;
  return REP_DIRECTORY.includes(repName.trim());
};

// Helper: Get display name for a rep (handles legacy IDs)
export const getRepDisplayName = (repName) => {
  if (!repName) return "";
  // If it's a legacy ID, convert to display name
  if (REP_DISPLAY_NAMES[repName]) {
    return REP_DISPLAY_NAMES[repName];
  }
  // Otherwise return as-is (should be a real name)
  return repName
};

// Aliases for backward compatibility
export const REP_NAMES = REP_DIRECTORY;
export const REPS = LEGACY_REP_IDS;

// Rep → Area assignment mapping (using real names)
export const REP_AREA_MAPPING = {
  "Anel Swart": ["PTA"],
  "Garth Bolton": ["JHB", "CPT"],
  "Frans Schutte": ["PE"],
  "Corine Wheeler": ["WTB"],
  "Lian Botha": ["PTA", "JHB"]  // Adjust as needed
};

// Helper: Get rep ID from display name (reverse lookup)
export const getRepIdFromDisplayName = (displayName) => {
  for (const [id, name] of Object.entries(REP_DISPLAY_NAMES)) {
    if (name === displayName) return id;
  }
  return null;
};

// Helper: Get allowed areas for a rep
export const getAllowedAreasForRep = (repName) => {
  if (!repName) return AREAS; // Return all if no rep specified
  return REP_AREA_MAPPING[repName] || AREAS;
};

// Helper: Check if rep is allowed to use an area
export const isRepAllowedInArea = (repName, area) => {
  if (!repName || !area) return true;
  const allowedAreas = REP_AREA_MAPPING[repName];
  if (!allowedAreas) return true; // Unknown rep can access all
  return allowedAreas.includes(area);
};

// Helper: Get default area for a rep (first in their list)
export const getDefaultAreaForRep = (repName) => {
  if (!repName) return "";
  const allowedAreas = REP_AREA_MAPPING[repName];
  return allowedAreas ? allowedAreas[0] : "";
};

// Helper: Map legacy area values to new ones
export const mapLegacyArea = (area) => {
  if (!area) return "";
  // If it's already a valid area, return it
  if (AREAS.includes(area)) return area;
  // Legacy mapping (if needed)
  const legacyMapping = {
    "Other": "PTA",  // Default legacy "Other" to PTA
  };
  return legacyMapping[area] || area; // Return original if no mapping
};

// Helper: Format area for display (handles legacy values)
export const formatAreaDisplay = (area) => {
  if (!area) return "-";
  if (AREAS.includes(area)) return area;
  return `${area}`; // Show legacy value as-is
};

// ============ ENTRY TYPE CONSTANTS ============
// Entry types for the Entries page (formerly Visits)
export const ENTRY_TYPES = ["visit", "appointment"];

// Visit sub-types (used when entry_type = "visit")
export const VISIT_TYPES = ["Drop Off", "Spec Call", "Detailed Call", "Follow Up", "Patient Feedback"];

// Appointment sub-types (used when entry_type = "appointment")
export const APPOINTMENT_TYPES = ["Lunch App", "F2F", "Coffee", "Breakfast"];

// ============ AREA LOCATION HIERARCHY ============
// Three-tier location: Region → Sub-area → Suburb

export const AREA_SUBREGIONS = {
  "PTA": [
    "CBD / Central",
    "Pretoria East — Old East",
    "Pretoria East — New East",
    "Pretoria East — Far East",
    "Centurion",
    "Pretoria North",
    "The Moot",
    "Montana / Sinoville",
    "Pretoria West",
    "Far North / Wonderboom",
    "Far East / Surrounds",
    "Townships"
  ],
  "JHB": [
    "Sandton",
    "Randburg",
    "Fourways / Dainfern",
    "Rosebank / Parktown",
    "Inner North / Emmarentia",
    "Joburg CBD / Inner City",
    "Eastern Suburbs",
    "East Rand",
    "Roodepoort",
    "Soweto",
    "Joburg South",
    "Midrand",
    "Alexandra / Surrounds"
  ],
  "CPT": [
    "City Bowl / CBD",
    "V&A Waterfront / Green Point",
    "Atlantic Seaboard",
    "Woodstock / Salt River",
    "Southern Suburbs — Upper",
    "Southern Suburbs — Lower",
    "Constantia Valley",
    "Southern Peninsula",
    "Northern Suburbs — South",
    "Northern Suburbs — North",
    "West Coast / Blaauwberg",
    "Cape Flats",
    "Helderberg / East"
  ],
  "WTB": [
    "CBD / Town Centre",
    "West / Northwest",
    "East",
    "South",
    "Outlying / Estates"
  ],
  "PE": [
    "CBD / Central",
    "Northern Areas",
    "North / Uitenhage Road",
    "Northwest",
    "South / Beachfront",
    "Suburbs — Mid",
    "Suburbs — Upmarket",
    "East / Swartkops",
    "Townships",
    "Outlying"
  ]
};

export const AREA_SUBURBS = {
  "PTA": {
    "CBD / Central": ["Arcadia", "Sunnyside", "Hatfield", "Muckleneuk", "Groenkloof", "Clydesdale", "Eastwood", "Colbyn", "Riviera", "Rietondale"],
    "Pretoria East — Old East": ["Waterkloof", "Waterkloof Ridge", "Waterkloof Glen", "Brooklyn", "Menlo Park", "Lynnwood", "Lynnwood Ridge", "Lynnwood Glen", "Hazelwood", "Constantia Park"],
    "Pretoria East — New East": ["Garsfontein", "Faerie Glen", "Moreleta Park", "Menlyn", "Newlands", "Meyerspark", "Murrayfield", "Brummeria", "De Wilgers"],
    "Pretoria East — Far East": ["Silver Lakes", "Woodhill", "Wapadrand", "Equestria", "Six Fountains", "Rietvalleirand", "Tierpoort", "Silverton"],
    "Centurion": ["Irene", "Lyttelton", "Eldoraigne", "Doringkloof", "Die Hoewes", "Zwartkop", "Wierdapark", "Rooihuiskraal", "The Reeds", "Highveld", "Midstream", "Hennopspark"],
    "Pretoria North": ["Akasia", "Karenpark", "Ninapark", "Dorandia", "Theresapark", "Florauna", "Orchards", "Chantelle", "Heatherdale", "Amandasig"],
    "The Moot": ["Waverley", "Kilnerpark", "Queenswood", "Villieria", "Gezina", "Wonderboom South", "Claremont", "Capitalpark", "Rietfontein", "Montanapark", "Mayville", "Moregloed"],
    "Montana / Sinoville": ["Montana", "Sinoville", "Annlin", "Magalieskruin", "Doornpoort", "Les Marais"],
    "Pretoria West": ["Pretoria West", "Daspoort", "Erasmia", "Atteridgeville (adjacent)"],
    "Far North / Wonderboom": ["Wonderboom", "Kameeldrift", "Shere", "Bultfontein"],
    "Far East / Surrounds": ["Rayton", "Cullinan", "Donkerhoek", "Tweefontein"],
    "Townships": ["Mamelodi", "Soshanguve", "Ga-Rankuwa", "Mabopane", "Hammanskraal", "Temba"]
  },
  "JHB": {
    "Sandton": ["Sandton CBD", "Sandhurst", "Bryanston", "Morningside", "Rivonia", "Hyde Park", "Illovo", "Melrose", "Melrose Arch", "Sandown", "Paulshof", "Sunninghill", "Strathavon"],
    "Randburg": ["Ferndale", "Northcliff", "Linden", "Bordeaux", "Fontainebleau", "Randpark Ridge", "Olivedale", "North Riding", "Robindale", "Blairgowrie", "Malanshof"],
    "Fourways / Dainfern": ["Fourways", "Dainfern", "Kyalami", "Broadacres", "Lonehill", "Chartwell", "Cranberry", "Witkoppen"],
    "Rosebank / Parktown": ["Rosebank", "Parktown", "Westcliff", "Saxonwold", "Houghton", "Dunkeld", "Craighall", "Craighall Park", "Norwood", "Orange Grove"],
    "Inner North / Emmarentia": ["Emmarentia", "Greenside", "Melville", "Parkview", "Auckland Park", "Westdene", "Linden", "Victory Park", "Parkhurst", "Oaklands"],
    "Joburg CBD / Inner City": ["Braamfontein", "Newtown", "Marshalltown", "Hillbrow", "Berea", "Yeoville", "Maboneng", "Doornfontein", "Fordsburg"],
    "Eastern Suburbs": ["Bedfordview", "Edenvale", "Kensington", "Cyrildene", "Observatory", "Linksfield", "Bezuidenhout Valley", "Jeppestown", "Eastgate"],
    "East Rand": ["Germiston", "Boksburg", "Benoni", "Kempton Park", "Brakpan", "Springs", "Alberton", "Modderfontein"],
    "Roodepoort": ["Florida", "Little Falls", "Weltevreden Park", "Constantia Kloof", "Radiokop", "Wilgeheuwel", "Honeydew", "Ruimsig", "Strubenvale"],
    "Soweto": ["Dobsonville", "Diepkloof", "Protea Glen", "Orlando", "Meadowlands", "Naledi", "Pimville", "Jabulani", "Chiawelo", "Kliptown", "Dlamini", "Zondi"],
    "Joburg South": ["Mulbarton", "Mondeor", "Glenvista", "Meyersdal", "Bassonia", "Winchester Hills", "Oakdene", "Turffontein", "Rosettenville", "Ormonde", "Kibler Park"],
    "Midrand": ["Halfway House", "Waterfall", "Carlswald", "Vorna Valley", "Sunningdale", "Kyalami (north)", "Midridge"],
    "Alexandra / Surrounds": ["Alexandra", "Wynberg", "Marlboro", "Bramley"]
  },
  "CPT": {
    "City Bowl / CBD": ["Cape Town CBD", "Foreshore", "Bo-Kaap", "De Waterkant", "Gardens", "Tamboerskloof", "Oranjezicht", "Vredehoek", "Signal Hill"],
    "V&A Waterfront / Green Point": ["Waterfront", "Green Point", "Mouille Point", "Fresnaye"],
    "Atlantic Seaboard": ["Sea Point", "Three Anchor Bay", "Clifton", "Camps Bay", "Bakoven", "Llandudno", "Hout Bay"],
    "Woodstock / Salt River": ["Woodstock", "Salt River", "Observatory", "Maitland", "Ndabeni"],
    "Southern Suburbs — Upper": ["Rondebosch", "Newlands", "Claremont", "Kenilworth", "Bishopscourt", "Mowbray", "Rosebank", "Pinelands"],
    "Southern Suburbs — Lower": ["Wynberg", "Plumstead", "Ottery", "Lansdowne", "Diep River", "Meadowridge", "Bergvliet", "Tokai", "Steenberg", "Retreat", "Heathfield"],
    "Constantia Valley": ["Constantia", "Meadowridge", "Westlake", "Silverhurst", "Wynberg upper"],
    "Southern Peninsula": ["Muizenberg", "St James", "Kalk Bay", "Fish Hoek", "Simon's Town", "Glencairn", "Kommetjie", "Noordhoek", "Scarborough"],
    "Northern Suburbs — South": ["Goodwood", "Parow", "Panorama", "Plattekloof", "Edgemead", "Tygervalley", "Welgemoed"],
    "Northern Suburbs — North": ["Bellville", "Durbanville", "Brackenfell", "Kraaifontein", "Kuils River", "Protea Heights", "Joostenberg Vlakte"],
    "West Coast / Blaauwberg": ["Milnerton", "Bloubergstrand", "Table View", "Parklands", "Melkbosstrand", "Century City", "Montague Gardens", "Killarney Gardens"],
    "Cape Flats": ["Athlone", "Mitchells Plain", "Hanover Park", "Manenberg", "Gugulethu", "Langa", "Khayelitsha", "Delft", "Philippi", "Grassy Park", "Lavender Hill"],
    "Helderberg / East": ["Somerset West", "Strand", "Gordon's Bay", "Macassar", "Lwandle", "Sir Lowry's Pass Village"]
  },
  "WTB": {
    "CBD / Town Centre": ["Witbank CBD", "Holfontein", "Die Heuwel", "Fern Avenue"],
    "West / Northwest": ["Highveld Park", "Del Judor", "Reyno Ridge", "Clewer", "Cassian"],
    "East": ["Tasbetpark", "Kwa-Guqa", "Ferrobank", "Ackerville"],
    "South": ["Witbank Ext areas", "Rietfontein", "Boschdal"],
    "Outlying / Estates": ["Landal", "Hlalanikahle", "Ogies (nearby)", "Middelburg (adjacent town)"]
  },
  "PE": {
    "CBD / Central": ["PE CBD", "Central", "Humewood", "Summerstrand", "Walmer", "Mill Park"],
    "Northern Areas": ["Gelvandale", "Bethelsdorp", "Booysens Park", "Helenvale", "Windvogel", "Cleary Park"],
    "North / Uitenhage Road": ["Kabega Park", "Parsons Hill", "Perridgevale", "Framesby", "Sunridge Park", "Lorraine"],
    "Northwest": ["Uitenhage (adjacent town)", "Despatch", "Kwanobuhle"],
    "South / Beachfront": ["Humewood", "Summerstrand", "Kings Beach", "Brookes Hill", "Pollok Beach"],
    "Suburbs — Mid": ["Mill Park", "Charlo", "Walmer", "Walmer Heights", "Mount Croix", "Newton Park", "Groenkloof"],
    "Suburbs — Upmarket": ["Kamma Park", "Lovemore Heights", "Lovemore Park", "Malabar", "Linkside"],
    "East / Swartkops": ["Swartkops", "Markman", "Amsterdamhoek", "Blue Horizon Bay"],
    "Townships": ["New Brighton", "Zwide", "Motherwell", "KwaDwesi", "KwaZakhele", "Soweto-on-Sea"],
    "Outlying": ["Jeffreys Bay (nearby)", "Uitenhage", "Despatch"]
  }
};


// ============ NEW CITY → AREA → SUBURB HIERARCHY ============
export const CITY_AREA_SUBURB = {
  "Centurion": {
    "Centurion East": ["Cornwall Hill", "Die Hoewes", "Doringkloof", "Glen Lauriston", "Hennopspark", "Heuweloord", "Highveld", "Irene", "Kosmosdal", "Lyttelton", "Lyttelton Manor", "Midstream", "Pierre van Ryneveld", "Southdowns", "Sunderland Ridge", "Zwartkop"],
    "Centurion North": ["Amberfield", "Centurion CBD", "Clubview", "Mnandi", "Valhalla"],
    "Centurion South": ["Olievenhoutbosch", "Rooihuiskraal", "Rooihuiskraal North", "The Reeds"],
    "Centurion West": ["Eldoraigne", "Kloofsig", "Laudium", "Monavoni", "Raslouw", "Thatchfield", "Wierda Park"],
  },
  "East Rand": {
    "Alberton": ["Alberton CBD", "Alberton North", "Brackendowns", "Brackengate", "Eden Glen", "Florentia", "Greenfields", "Hospitaalpark", "Meyersdal", "New Redruth", "Randhart", "Roodekop", "Thokoza", "Verwoerdpark"],
    "Bedfordview": ["Bedfordview", "Bedfordview East", "Lambton"],
    "Benoni": ["Actonville", "Bapsfontein", "Benoni CBD", "Benoni North", "Benoni South", "Crystal Park", "Daveyton", "Etwatwa", "Farrarmere", "Lakefield", "Northmead", "Rynfield", "Wattville"],
    "Boksburg": ["Bartlett", "Boksburg CBD", "Boksburg East", "Boksburg North", "Boksburg South", "Boksburg West", "Dawn Park", "Jet Park", "Klippoortjie", "Reiger Park", "Sunward Park", "Vosloorus"],
    "Brakpan": ["Brakpan CBD", "Brakpan North", "Dalpark", "Leachville", "Tsakane"],
    "Edenvale": ["Edenvale CBD", "Greenstone Hill", "Hayfields", "Modderfontein", "Norkem Park", "Spartan"],
    "Germiston": ["Dinwiddie", "Eastleigh", "Elsburg", "Germiston CBD", "Germiston South", "Katlehong", "Leondale", "Likole", "Primrose", "Primrose Hill", "Sunnyridge", "Webber"],
    "Heidelberg": ["Heidelberg"],
    "Kempton Park": ["Birchleigh", "Bonaero Park", "Edleen", "Esther Park", "Glen Erasmia", "Glen Marais", "Isando", "Kempton Park CBD", "Kempton Park North", "OR Tambo Airport Area", "Pomona", "Rhodesfield", "Serengeti Estate", "Terenure", "Van Riebeeck Park"],
    "Nigel": ["Dunnottar", "Nigel CBD", "Nigel East"],
    "Springs": ["Daggafontein", "Devon", "Duduza", "KwaThema", "Langaville", "Petersfield", "Pollak Park", "Selcourt", "Selection Park", "Springs CBD", "Springs East", "Strubenvale"],
  },
  "Far South": {
    "Heidelberg": ["Heidelberg CBD", "Ratanda", "Suikerbos"],
    "Meyerton": ["Golf Park", "Highbury", "Meyerton CBD", "Meyerton Central", "Meyerton North"],
    "Vanderbijlpark": ["Boipatong", "Bophelong", "Flora Gardens", "Vanderbijlpark"],
    "Vereeniging": ["Arcon Park", "Bedworth Park", "Duncanville", "Evaton", "Falcon Ridge", "Geluksdal", "Mamello", "Peacehaven", "Roshnee", "Sebokeng", "Sharpeville", "Three Rivers", "Unitas Park", "Vaalpark", "Vereeniging CBD", "Waldrift"],
  },
  "Johannesburg": {
    "Johannesburg Central": ["Bellevue", "Berea", "Bertrams", "Braamfontein", "Doornfontein", "Fordsburg", "Hillbrow", "Jeppestown", "Johannesburg CBD", "Joubert Park", "Maboneng", "Mayfair", "Newtown", "Observatory", "Pageview", "Parktown", "Troyeville", "Yeoville"],
    "Johannesburg East": ["Alexandra", "Bramley", "Bruma", "Cyrildene", "Edenvale", "Fairway", "Gallo Manor", "Glenhazel", "Highlands North", "Houghton Estate", "Kensington", "Kew", "Killarney", "Linksfield", "Lombardy East", "Malvern", "Norwood", "Oaklands", "Orange Grove", "Raedene", "Sandringham", "Savoy Estate", "Sydenham", "Victoria", "Wynberg"],
    "Johannesburg North": ["Bloubosrand", "Broadacres", "Chartwell", "Diepsloot", "Fourways", "Kya Sand", "Lonehill", "Magaliessig", "Northgate"],
    "Johannesburg South": ["Aspen Hills", "Bassonia", "Crown Gardens", "Elandspark", "Glenanda", "Glenvista", "Haddon", "Kibler Park", "La Rochelle", "Lenasia", "Liefde en Vrede", "Linmeyer", "Meredale", "Mondeor", "Mulbarton", "Naturena", "Oakdene", "Ormonde", "Ridgeway", "Robertsham", "Rosettenville", "South Hills", "Southdale", "Southgate", "Turffontein", "Winchester Hills"],
    "Randburg": ["Albertskroon", "Albertville", "Auckland Park", "Berario", "Blackheath", "Blairgowrie", "Bordeaux", "Brixton", "Craighall", "Craighall Park", "Cramerview", "Cresta", "Darrenwood", "Emmarentia", "Fairland", "Ferndale", "Florida Glen", "Fontainebleau", "Greenside", "Greymont", "Hurlingham", "Hyde Park", "Linden", "Malanshof", "Melville", "Newlands", "Northcliff", "Northriding", "Olivedale", "Parkhurst", "Parkmore", "Parktown North", "Parkview", "Randburg CBD", "Randpark Ridge", "Rosebank", "Sandhurst", "Sophiatown", "Strijdompark", "Westcliff", "Westdene"],
    "Roodepoort": ["Bosmont", "Constantia Kloof", "Discovery", "Dobsonville", "Florida", "Florida Hills", "Florida Lake", "Honeydew", "Jukskei Park", "Little Falls", "Roodepoort CBD", "Ruimsig", "Weltevredenpark", "Zandspruit"],
    "Sandton": ["Atholl", "Benmore Gardens", "Birdhaven", "Bryanston", "Bryanston East", "Chislehurston", "Douglasdale", "Edenburg", "Illovo", "Inanda", "Kelvin", "Kramerville", "Linbro Park", "Marlboro", "Melrose", "Melrose Arch", "Melrose Estate", "Melrose North", "Morningside", "Morningside Manor", "Rivonia", "Sandown", "Sandton CBD", "Saxonwold", "Strathavon", "Sunninghill", "Wierda Valley", "Witkoppen", "Woodlands"],
    "Soweto": ["Chiawelo", "Diepkloof", "Doornkop", "Eldorado Park", "Ennerdale", "Kliptown", "Meadowlands", "Noordgesig", "Orange Farm", "Orlando", "Orlando East", "Orlando West", "Phiri", "Protea Glen", "Soweto CBD", "Zola"],
  },
  "Midrand": {
    "Midrand North": ["Buccleuch", "Carlswald", "Crowthorne", "Dainfern", "Ebony Park", "Erand Gardens", "Glen Austin", "Ivory Park", "Kyalami", "Maroeladal", "Midrand CBD", "Midridge Park", "Noordwyk", "Olifantsfontein", "Paulshof", "Rabie Ridge", "Randjesfontein AH", "Randjespark", "Tembisa", "Vorna Valley", "Willaway"],
    "Midrand South": ["Halfway Gardens", "Headway Hill", "Summerset Estate", "Waterfall", "Waterfall City", "Waterval City", "Woodmead"],
  },
  "Pretoria": {
    "Centurion South": ["Erasmia"],
    "Far East": ["Bashewa", "Boschkop", "Bronkhorstspruit", "Cullinan", "Derdepoort", "Grootfontein", "Kameeldrift", "Kleinfontein", "Lindopark", "Mooiplaats AH", "Rayton", "Rietvlei View", "Roodeplaat", "Tierpoort"],
    "Far North": ["Boekenhoutskloof", "Bon Accord", "Buffelsdrift AH", "Bultfontein AH", "De Wagendrif", "Dinokeng", "Doornpoort", "Grootvlei", "Hammanskraal", "Pyramid", "Rooiwal", "Temba", "Wallmannsthal", "Waterval", "Welgevonden"],
    "Moot": ["Capital Park", "Colbyn", "Gezina", "Kilner Park", "Moregloed", "Queenswood", "Rietfontein", "Rietondale", "Riviera", "Villieria", "Waverley", "Wonderboom South"],
    "Pretoria Central": ["Arcadia", "Ashlea Gardens", "Brooklyn", "Clydesdale", "Eastwood", "Erasmus Park", "Erasmusrand", "Groenkloof", "Hatfield", "Hazelwood", "Hillcrest", "Lukasrand", "Maroelana", "Menlo Park", "Monument Park", "Muckleneuk", "Pretoria CBD", "Sterrewag", "Sunnyside", "Waterkloof", "Waterkloof Heights", "Waterkloof Park", "Waterkloof Ridge"],
    "Pretoria East": ["Boardwalk", "Bronberg", "Brummeria", "Constantia Park", "Die Wilgers", "Eersterust", "Elarduspark", "Equestria", "Erasmuskloof", "Faerie Glen", "Garsfontein", "Glenwood", "Hazeldean", "Lynnwood", "Lynnwood Glen", "Lynnwood Manor", "Lynnwood Ridge", "Mamelodi", "Menlyn", "Meyerspark", "Mooikloof", "Moreleta Park", "Murrayfield", "Newlands", "Olympus", "Pretorius Park", "Rietvalleirand", "Silver Stream", "Silverton", "The Meadows", "The Meadows at Hazeldean", "The Wilds", "Tijger Valley", "Val de Grace", "Waltloo", "Wapadrand", "Waterkloof Glen", "Weavind Park", "Welbekend", "Willow Acres", "Willow Glen", "Willow Park Manor", "Wingate Park"],
    "Pretoria North": ["Akasia", "Amandasig", "Annlin", "Chantelle", "Dorandia", "Eloffsdal", "Florauna", "Ga-Rankuwa", "Heatherdale", "Heatherview", "Hesteapark", "Karen Park", "Klerksoord", "Les Marais", "Mabopane", "Magalieskruin", "Mayville", "Montana", "Montana Gardens", "Montana Park", "Ninapark", "Onderstepoort", "Pretoria North CBD", "Roseville", "Rosslyn", "Sinoville", "Soshanguve", "Strydfontein", "The Orchards", "Theresa Park", "Tileba", "Wolmer", "Wonderboom"],
    "Pretoria West": ["Andeon AH", "Atteridgeville", "Booysens", "Claremont", "Danville", "Daspoort", "Elandspoort", "Hercules", "Hermanstad", "Kirkney", "Kwaggasrand", "Lotus Gardens", "Mountain View", "Philip Nel Park", "Pretoria Gardens", "Pretoria West CBD", "Proclamation Hill", "Saulsville", "Suiderberg", "Wespark"],
  },
  "West Rand": {
    "Krugersdorp": ["Azaadville", "Chamdor", "Dan Pienaarville", "Dersley", "Factreton", "Kagiso", "Kenmare", "Krugersdorp CBD", "Krugersdorp North", "Krugersdorp West", "Monument", "Muldersdrift", "Munsieville", "Noordheuwel", "Rant en Dal", "Silverfields", "Wilgeheuwel", "Witpoortjie"],
    "Randfontein": ["Homelake", "Mohlakeng", "Randfontein CBD", "Randfontein East", "Tarlton", "Zuurbekom"],
    "West Rand Surrounds": ["Carletonville", "Fochville", "Khutsong", "Magaliesburg", "Oberholzer", "Westonaria"],
  },
};

export const CITIES = ["Centurion", "East Rand", "Far South", "Johannesburg", "Midrand", "Pretoria", "West Rand"];

export const CITY_AREAS = {
  "Centurion": ["Centurion East", "Centurion North", "Centurion South", "Centurion West"],
  "East Rand": ["Alberton", "Bedfordview", "Benoni", "Boksburg", "Brakpan", "Edenvale", "Germiston", "Heidelberg", "Kempton Park", "Nigel", "Springs"],
  "Far South": ["Heidelberg", "Meyerton", "Vanderbijlpark", "Vereeniging"],
  "Johannesburg": ["Johannesburg Central", "Johannesburg East", "Johannesburg North", "Johannesburg South", "Randburg", "Roodepoort", "Sandton", "Soweto"],
  "Midrand": ["Midrand North", "Midrand South"],
  "Pretoria": ["Centurion South", "Far East", "Far North", "Moot", "Pretoria Central", "Pretoria East", "Pretoria North", "Pretoria West"],
  "West Rand": ["Krugersdorp", "Randfontein", "West Rand Surrounds"],
};

// Helper: given a suburb name, return {city, area, suburb}
export const getLocationFromSuburb = (suburbName) => {
  for (const city in CITY_AREA_SUBURB) {
    for (const area in CITY_AREA_SUBURB[city]) {
      if (CITY_AREA_SUBURB[city][area].includes(suburbName)) {
        return { city, area, suburb: suburbName };
      }
    }
  }
  return null;
};

// Helper: get all suburbs sorted
export const getAllSuburbs = () => {
  const suburbs = [];
  for (const city in CITY_AREA_SUBURB) {
    for (const area in CITY_AREA_SUBURB[city]) {
      suburbs.push(...CITY_AREA_SUBURB[city][area]);
    }
  }
  return suburbs.sort();
};
